import { useState, useEffect } from 'react'
import { CreditCard, Coins, FileText, Check, ArrowRight, Receipt, ArrowLeft, Info } from 'lucide-react'
import ServiceTokenModal from '../components/ServiceTokenModal'
import { useToast } from '../lib/ToastContext'
import { formatCurrency } from '../lib/format'
import { subscriptionsApi, tokensApi, invoicesApi, paymentsApi } from '../lib/api'
import { PLANS, getPlan } from '../data/pricing'

function getPlanRank(planId) {
  // ✅ Safety check - if PLANS is undefined, return -1
  if (!PLANS || !Array.isArray(PLANS)) return -1
  return PLANS.findIndex((p) => p.id === planId)
}

const invoiceStatusBadge = (s) => {
  const map = {
    Paid: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    Pending: 'border-amber-300 text-amber-700 bg-amber-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
}

export default function Billing({ currentPlan = 'seed', onNavigateBilling, companyId, onNavigate }) {
  const [plan, setPlan] = useState(currentPlan)
  const [tokens, setTokens] = useState(0)
  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [renewalDate, setRenewalDate] = useState('—')
  const [subStatus, setSubStatus] = useState('Inactive')
  const [upgrading, setUpgrading] = useState(false)
  const { toast } = useToast()

  // ✅ Safety check for PLANS
  const plans = PLANS && Array.isArray(PLANS) ? PLANS : []

  useEffect(() => {
    if (!companyId) return
    let mounted = true
    ;(async () => {
      const { data: sub } = await subscriptionsApi.get(companyId)
      if (!mounted) return
      if (sub?.plan) setPlan(sub.plan)
      if (sub?.renewal_date) setRenewalDate(sub.renewal_date)
      if (sub?.status) setSubStatus(sub.status.charAt(0).toUpperCase() + sub.status.slice(1))
      const { data: wallet } = await tokensApi.getWallet(companyId)
      if (!mounted) return
      if (wallet?.available != null) setTokens(wallet.available)
      const { data: invData } = await invoicesApi.list(companyId)
      if (!mounted) return
      if (invData && invData.length > 0) {
        setInvoices(invData.map((inv) => ({
          id: inv.invoice_id,
          service: inv.service_type,
          amount: inv.amount,
          date: inv.date,
          status: inv.status.charAt(0).toUpperCase() + inv.status.slice(1),
        })))
      }
    })()
    return () => { mounted = false }
  }, [companyId])

  // ✅ Safety check for getPlan
  const activePlan = plans.length > 0 ? getPlan(plan) : { name: 'Seed', price: 15000 }

  const handleUpgrade = async (newPlan) => {
    if (newPlan === plan) return
    setUpgrading(true)
    if (companyId) {
      const callbackUrl = window.location.origin + '?view=billing&payment=return'
      const { data, error } = await paymentsApi.initialize(newPlan, companyId, callbackUrl)
      setUpgrading(false)
      if (error) {
        toast('Upgrade failed: ' + error, 'error')
        return
      }
      if (data?.authorization_url) {
        window.location.href = data.authorization_url
        return
      }
    }
    setPlan(newPlan)
    setUpgrading(false)
    toast(`Workspace upgraded to ${getPlan(newPlan).name}.`, 'success')
  }

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-ink">Billing &amp; Subscription</h1>
        <p className="text-sm text-muted mt-1">Manage your workspace plan, agent tokens, and billing history.</p>
      </div>

      {/* Subscription clarification */}
      <div className="p-5 rounded-xl bg-blue-50 border border-blue-200 flex items-start gap-4">
        <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-ink mb-1">What your subscription covers</p>
          <p className="text-xs text-neutral-600 leading-relaxed">
            Your monthly subscription pays for access to the Bizos Suite platform — your document vault, staff management, governance scoring, investor portal, and audit trail. It also unlocks specific document categories based on your plan. Documents you already have can be uploaded and stored at no extra cost. Some specialized documents may require a separate one-time payment.
          </p>
        </div>
      </div>

      {/* Active Plan Card */}
      <div className="surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><CreditCard size={15} /> Active Plan</h3>
          <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-semibold border ${subStatus === 'Active' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{subStatus}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">Plan Name</p>
            <p className="text-sm font-semibold text-ink mt-1">{activePlan.name}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">Price</p>
            <p className="text-sm font-mono text-ink mt-1">{formatCurrency(activePlan.price, 'NGN')}/mo</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">Renewal Date</p>
            <p className="text-sm font-mono text-ink mt-1">{renewalDate}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">Status</p>
            <p className={`text-sm mt-1 ${subStatus === 'Active' ? 'text-emerald-700' : 'text-neutral-500'}`}>{subStatus}</p>
          </div>
        </div>
      </div>

      {/* Compliance Agent Wallet */}
      <div className="surface p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-soft flex items-center justify-center">
              <Coins size={18} className="text-brand" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink">Compliance Agent Wallet</h3>
              <p className="text-xs text-muted mt-0.5">Service tokens for localized legal assistance</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold text-ink font-mono">{tokens}</p>
              <p className="text-[11px] text-muted">Available Tokens</p>
            </div>
            <button
              onClick={() => setTokenModalOpen(true)}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <Coins size={13} /> Buy Agent Service Token
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Tiers */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">Subscription Tiers</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => {
            const isCurrent = p.id === plan
            return (
              <div
                key={p.id}
                className={`surface p-6 flex flex-col ${p.highlighted ? 'border-brand border-2' : ''}`}
              >
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-ink">{p.name}</h4>
                  {p.highlighted && <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border border-brand-soft bg-brand-soft text-brand mt-1.5">Popular</span>}
                </div>
                <p className="text-2xl font-bold text-ink font-mono mb-1">{formatCurrency(p.price, 'NGN')}<span className="text-xs font-normal text-muted">/mo</span></p>
                <div className="space-y-2 my-4 flex-1">
                  {/* ✅ Safety check for features */}
                  {p.features && p.features.map((f) => (
                    <div key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                      <span className="text-xs text-neutral-600">{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleUpgrade(p.id)}
                  disabled={isCurrent || upgrading}
                  className={isCurrent ? 'btn-secondary text-xs w-full justify-center' : 'btn-primary text-xs w-full justify-center flex items-center gap-1.5'}
                >
                  {isCurrent
                    ? 'Current Plan'
                    : upgrading
                      ? 'Processing…'
                      : getPlanRank(p.id) < getPlanRank(plan)
                        ? 'Downgrade'
                        : <>Upgrade <ArrowRight size={12} /></>}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Recent Invoices */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2"><Receipt size={15} /> Recent Invoices &amp; Filings</h3>
        <div className="surface">
          <div className="grid grid-cols-12 px-6 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500">
            <div className="col-span-3">Invoice ID</div>
            <div className="col-span-4">Service Type</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Status</div>
          </div>
          {invoices.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <Receipt size={24} className="text-neutral-300 mx-auto mb-2" />
              <p className="text-sm text-muted">Invoices will appear here once you make a payment.</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {invoices.map((inv) => (
                <div key={inv.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                  <div className="col-span-3 font-mono text-xs text-ink">{inv.id}</div>
                  <div className="col-span-4 text-neutral-600">{inv.service}</div>
                  <div className="col-span-2 text-right font-mono text-xs text-ink">{formatCurrency(inv.amount, 'NGN')}</div>
                  <div className="col-span-2 font-mono text-xs text-neutral-500">{inv.date}</div>
                  <div className="col-span-1 text-right">{invoiceStatusBadge(inv.status)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ServiceTokenModal
        open={tokenModalOpen}
        onClose={() => setTokenModalOpen(false)}
        onPurchase={async (count) => {
          setTokens((prev) => prev + count)
          if (companyId) {
            await tokensApi.purchase(companyId, count, count === 5 ? 60000 : 15000)
          }
        }}
      />
    </div>
  )
}